// apps/client/src/ui/creator/CreatorForm.tsx (M0-T16) — name + look tabs over a live preview; the screen owns saving
import { friendColors, look, tuning, type FriendColor } from '@whiskerton/content';
import type { Look } from '@whiskerton/shared';
import { useMemo, useState } from 'react';
import { lookToDoll } from '../../characters/look';
import { useT } from '../i18n';
import { Button, Panel, Tabs, TextField } from '../kit';
import { Preview } from './Preview';
import { SwatchRow } from './SwatchRow';

const CSS = `
.creator { display: grid; grid-template-columns: minmax(200px, 38%) 1fr; gap: 24px; align-items: center; height: 100%; }
.creator-preview { min-width: 0; }
.creator-canvas { display: block; width: 100%; aspect-ratio: 3 / 4; }
.creator .panel { display: flex; flex-direction: column; gap: 14px; }
/* Save stays in view on a landscape phone (§8.7): pinned to the foot of the panel's scroll box, over a cream fade, when the swatches
   run past it. Its negative margins (and offset) reach the panel's side and bottom padding, so it sits the same pinned or not. */
.creator-actions { position: sticky; bottom: -22px; z-index: 1; display: grid; margin: auto -22px -22px; padding: 16px 22px 22px;
  background: linear-gradient(to bottom, rgba(255,251,241,0), var(--cream-50) 16px); }
.swatch-row { border: 0; padding: 0; margin: 0; display: flex; flex-wrap: wrap; gap: 8px; }
.swatch-row legend { font-weight: 900; margin-bottom: 6px; }
.swatch { min-width: 40px; height: 40px; border: 0; border-radius: var(--r-swatch); background: var(--swatch, var(--cream-100)); font: inherit; font-weight: 800; padding: 0 10px; box-shadow: var(--sh-soft); cursor: pointer; }
.swatch[aria-pressed='true'] { box-shadow: inset 0 0 0 3px var(--leaf-700); }
`;
let styled = false;
function injectStyle(): void {
  if (styled || typeof document === 'undefined') return;
  styled = true;
  const el = document.createElement('style');
  el.dataset.owner = 'creator';
  el.textContent = CSS;
  document.head.appendChild(el);
}

type Tab = 'face' | 'hair' | 'clothes';
export function CreatorForm(p: { initial: { name: string; look: Look }; friend: FriendColor; saving: boolean; title?: string;
  onSave: (v: { name: string; look: Look }) => void }) {
  injectStyle();
  const t = useT();
  const [name, setName] = useState(p.initial.name);
  const [l, setLook] = useState<Look>(p.initial.look);
  const [tab, setTab] = useState<Tab>('face');
  const spec = useMemo(() => lookToDoll(l, p.friend), [l, p.friend]);
  const set = (k: keyof Look) => (id: string) => setLook({ ...l, [k]: id });
  const clean = name.replace(/\s+/g, ' ').trim();
  const nameOk = clean.length >= tuning.town.nameMinChars && clean.length <= tuning.town.nameMaxChars;
  const shirts = [{ id: 'friend', name: 'creator.myTownColor', hex: friendColors[p.friend].dye }, ...look.dyes];
  return (
    <div className="creator">
      <div className="creator-preview"><Preview spec={spec} /></div>
      <Panel title={p.title ?? t('creator.title')}>
        <TextField label={t('creator.nameLabel')} value={name} onChange={setName} maxLength={tuning.town.nameMaxChars}
          placeholder={t('creator.nameHint', { min: tuning.town.nameMinChars, max: tuning.town.nameMaxChars })} />
        <Tabs tabs={[{ id: 'face', label: t('creator.tabFace') }, { id: 'hair', label: t('creator.tabHair') }, { id: 'clothes', label: t('creator.tabClothes') }]}
          active={tab} onChange={(id) => setTab(id as Tab)} />
        {tab === 'face' && <>
          <SwatchRow label={t('creator.skin')} options={look.skins} value={l.skin} onPick={set('skin')} />
          <SwatchRow label={t('creator.eyes')} options={look.eyes} value={l.eyes} onPick={set('eyes')} />
        </>}
        {tab === 'hair' && <>
          <SwatchRow label={t('creator.hairStyle')} options={look.hairStyles} value={l.hairStyle} onPick={set('hairStyle')} />
          <SwatchRow label={t('creator.hairColor')} options={look.hairColors} value={l.hairColor} onPick={set('hairColor')} />
        </>}
        {tab === 'clothes' && <>
          <SwatchRow label={t('creator.shirt')} options={shirts} value={l.shirt} onPick={set('shirt')} />
          <SwatchRow label={t('creator.pants')} options={look.dyes} value={l.pants} onPick={set('pants')} />
          <SwatchRow label={t('creator.shoes')} options={look.dyes} value={l.shoes} onPick={set('shoes')} />
        </>}
        <div className="creator-actions">
          <Button disabled={!nameOk || p.saving} onClick={() => p.onSave({ name: clean, look: l })}>{p.saving ? t('creator.saving') : t('creator.save')}</Button>
        </div>
      </Panel>
    </div>
  );
}
